import { notifications } from '@mantine/notifications';
import monitoringStore from '@utils/monitoring/requestMonitoring';
import Axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, RawAxiosRequestConfig } from 'axios';
import { Api } from '../models/ropolo';
import getRefreshToken from '../utils/auth/getRefreshToken';
import { handleTokenRefresh, logoutAndRedirect } from '../utils/auth/getTokenByRefreshToken';
import setAxiosHeader from '../utils/auth/setAxiosHeader';
import { BASE_API_URL } from './config';

const offlineRopoloApi = new Api({
  baseURL: 'http://localhost:5500/',
  headers: {
    'Accept-Language': 'fa-IR',
  },
});

/*
Online request configuration
*/
export const ropoloApi = new Api({
  baseURL: BASE_API_URL,
  headers: {
    'Accept-Language': 'fa-IR',
  },
});

ropoloApi.instance.interceptors.request.use(
  async (config) => setAxiosHeader(config),
  (error) => {
    Promise.reject(error);
  }
);

ropoloApi.instance.interceptors.response.use(
  (response) => {
    const startTime = response.config.headers['Request-Start-Time'];
    const endTime = new Date().getTime();
    const requestTime = endTime - startTime;

    monitoringStore.updateMetrics(requestTime); // Update the store
    return response;
  },
  async (error) => {
    const startTime = error.config?.headers['Request-Start-Time'];
    const endTime = new Date().getTime();
    if (startTime) {
      const requestTime = endTime - startTime;
      monitoringStore.updateMetrics(requestTime); // Update the store
    }
    if (error.response && error.response.status === 401) {
      handleTokenRefresh();
    } else {
      const message = error?.response?.data?.data?.message;
      if (message) {
        notifications.show({
          message,
          color: 'red',
        });
      }
    }
    return Promise.reject(error);
  }
);

/*
Offline request configuration
*/
const ropoloServerCallingApi = new Api({
  baseURL: BASE_API_URL,
  headers: {
    'Accept-Language': 'fa-IR',
  },
});

ropoloServerCallingApi.instance.interceptors.request.use(
  async (config) => config,
  (error) => {
    Promise.reject(error);
  }
);

ropoloServerCallingApi.instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      console.log('unAuthorize');
    } else {
      const message = error?.response?.data?.data?.message;
      if (message) {
        notifications.show({
          message,
          color: 'red',
        });
      }
    }
    return Promise.reject(error);
  }
);

export const ropoloRequest = {
  server: {
    ...ropoloServerCallingApi.api,
    ...ropoloServerCallingApi.apiIdentity,
    ...ropoloServerCallingApi?.apiCatalog,
  },

  ...ropoloApi.api,
  ...ropoloApi.apiCatalog,
  ...ropoloApi.apiIdentity,
  ...ropoloApi?.apiCatalog,
  ...ropoloApi?.apiSnappFood,
  ...ropoloApi?.apiDelivery,
};

export const offlineRopoloRequest = {
  ...offlineRopoloApi.api,
  ...offlineRopoloApi.apiCatalog,
  ...offlineRopoloApi.apiIdentity,
  ...offlineRopoloApi?.apiCatalog,
  ...ropoloApi?.apiSnappFood,
};

export const setUrl = (url: string) => {
  ropoloApi.instance.defaults.baseURL = url;
};

export const api = Axios.create({
  baseURL: '/',
  headers: {
    'Accept-Language': 'fa-IR',
  },
});

api.interceptors.request.use(
  async (config) => setAxiosHeader(config),
  (error) => {
    Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        logoutAndRedirect();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

const request = {
  get: <T>(endpoint: string, options?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.get(endpoint, options),
  post: (endpoint: string, data?: unknown, options?: RawAxiosRequestConfig): Promise<AxiosResponse> =>
    api.post(endpoint, data, options),
  put: (endpoint: string, data: unknown, options?: AxiosRequestConfig): Promise<AxiosResponse> =>
    api.put(endpoint, data, options),
  patch: (endpoint: string, data?: unknown, options?: AxiosRequestConfig): Promise<AxiosResponse> =>
    api.patch(endpoint, { ...(data as Object) }, options),
  delete: (endpoint: string, data?: unknown, options?: AxiosRequestHeaders): Promise<AxiosResponse> =>
    api.delete(endpoint, {
      data,
      headers: options,
    }),
  options: (endpoint: string, options?: AxiosRequestConfig): Promise<AxiosResponse> => api.options(endpoint, options),
  head: (endpoint: string, options?: AxiosRequestConfig): Promise<AxiosResponse> => api.head(endpoint, options),
};

export default request;
